import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface BdAddressState {
  permCountry: string;
  permDivision: string;
  permDistrict: string;
  permUpazila: string;
  permUnionCity: string;
  permArea: string;
  permPostalCode: string;
  permFullAddress: string;

  sameAsPermanent: boolean;

  presCountry: string;
  presDivision: string;
  presDistrict: string;
  presUpazila: string;
  presUnionCity: string;
  presArea: string;
  presPostalCode: string;
  presFullAddress: string;
}

export const initialAddressState: BdAddressState = {
  permCountry: 'Bangladesh',
  permDivision: '',
  permDistrict: '',
  permUpazila: '',
  permUnionCity: '',
  permArea: '',
  permPostalCode: '',
  permFullAddress: '',

  sameAsPermanent: true,

  presCountry: 'Bangladesh',
  presDivision: '',
  presDistrict: '',
  presUpazila: '',
  presUnionCity: '',
  presArea: '',
  presPostalCode: '',
  presFullAddress: '',
};

const bdAddressSlice = createSlice({
  name: 'bdAddress',
  initialState: initialAddressState,
  reducers: {
    setPermField: (state, action: PayloadAction<{ field: keyof BdAddressState; value: string }>) => {
      const { field, value } = action.payload;
      (state as any)[field] = value;

      // Cascading reset logic for permanent address
      if (field === 'permDivision') {
        state.permDistrict = '';
        state.permUpazila = '';
        state.permUnionCity = '';
        if (state.sameAsPermanent) {
          state.presDivision = value;
          state.presDistrict = '';
          state.presUpazila = '';
          state.presUnionCity = '';
        }
      }
      if (field === 'permDistrict') {
        state.permUpazila = '';
        state.permUnionCity = '';
        if (state.sameAsPermanent) {
          state.presDistrict = value;
          state.presUpazila = '';
          state.presUnionCity = '';
        }
      }
      if (field === 'permUpazila') {
        state.permUnionCity = '';
        if (state.sameAsPermanent) {
          state.presUpazila = value;
          state.presUnionCity = '';
        }
      }
      if (field === 'permUnionCity' && state.sameAsPermanent) {
        state.presUnionCity = value;
      }
      if (field === 'permArea' && state.sameAsPermanent) {
        state.presArea = value;
      }
      if (field === 'permPostalCode' && state.sameAsPermanent) {
        state.presPostalCode = value;
      }
      if (field === 'permFullAddress' && state.sameAsPermanent) {
        state.presFullAddress = value;
      }
      if (field === 'permCountry' && state.sameAsPermanent) {
        state.presCountry = value;
      }
    },

    setPresField: (state, action: PayloadAction<{ field: keyof BdAddressState; value: string }>) => {
      const { field, value } = action.payload;
      (state as any)[field] = value;

      // Cascading reset logic for present address
      if (field === 'presDivision') {
        state.presDistrict = '';
        state.presUpazila = '';
        state.presUnionCity = '';
      }
      if (field === 'presDistrict') {
        state.presUpazila = '';
        state.presUnionCity = '';
      }
      if (field === 'presUpazila') {
        state.presUnionCity = '';
      }
    },

    setSameAsPermanent: (state, action: PayloadAction<boolean>) => {
      const same = action.payload;
      state.sameAsPermanent = same;
      if (same) {
        state.presCountry = state.permCountry;
        state.presDivision = state.permDivision;
        state.presDistrict = state.permDistrict;
        state.presUpazila = state.permUpazila;
        state.presUnionCity = state.permUnionCity;
        state.presArea = state.permArea;
        state.presPostalCode = state.permPostalCode;
        state.presFullAddress = state.permFullAddress;
      }
    },

    resetAddressState: () => initialAddressState,

    /**
     * Replaces stored legacy ids with readable names without triggering the
     * cascade resets of `setPermField`/`setPresField`. Used when an older
     * patient record is loaded: the hierarchy loads level by level, so a
     * not-yet-known child must keep its id until its parent list arrives.
     */
    setResolvedAddressNames: (state, action: PayloadAction<Partial<BdAddressState>>) => {
      const updates = action.payload;
      Object.assign(state, updates);
      if (state.sameAsPermanent) {
        for (const [key, value] of Object.entries(updates)) {
          if (!key.startsWith('perm')) continue;
          const presKey = `pres${key.slice(4)}` as keyof BdAddressState;
          (state as any)[presKey] = value;
        }
      }
    },
  },
});

export const {
  setPermField,
  setPresField,
  setSameAsPermanent,
  resetAddressState,
  setResolvedAddressNames,
} = bdAddressSlice.actions;

export default bdAddressSlice.reducer;
